import FormHelperText from '@mui/material/FormHelperText';
import { uploadToCloudinary } from 'apis/cloudinary';
import { IframelyApi } from 'apis/iframelyApi';
import { UpdateFormValues } from 'domain/Updates/shared/UpdateForm/types';
import { FormikHandlers, FormikHelpers } from 'formik';
import debounce from 'lodash.debounce';
import MagicUrl from 'quill-magic-url';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill, { Quill, Range } from 'react-quill';
import 'quill-mention/dist/quill.mention.css';
import 'quill-mention';
import 'styles/global/overrides/quill-editor-overrides.scss';
import { CloudinaryUploadSettings } from 'types';
import BlockUi from 'ui/elements/form/RichTextEditor/components/BlockUi';
import EmbedDialog from './components/EmbedDialog';
import './components/IframelyBlot';
import './components/ImageUpload';
import './components/GridBlot';
import './components/BlockBlot';
import './components/MentionBlot';
import './components/LineBreakBlot';
import './components/BlockquoteBlot';
import './components/CompanyBlot';
import './components/UpdateBlot';
import './components/KpiBlot';
import './components/LinkSanitizer';
import './components/ImageMenu';
import { AdvancedToolbar } from './components/Toolbar';
import { createImageClipboardMatcher } from './components/ImageClipboardMatcher';
import { isIframe, mentionModule, parseEmbed, stripEmbed } from './utils';
import isEmpty from 'ramda/src/isEmpty';
import EmojiPicker from 'ui/elements/EmojiPicker';
import EditLinkPreview from './components/EditLinkPreview';
import { contentSpacing, halfSpacing, quarterSpacing } from 'ui/theme/themeConstants';
import styled from '@emotion/styled';
import UserProfileDialog from 'domain/users/UserProfile/UserProfileDialog';
import PublicProfileDialog from 'domain/companies/ProfileDialog/ProfileDialog';
import useClickableMentions from './useClickableMentions';
import MentionSelect from 'ui/elements/MentionSelect';
import { Hidden, useMediaQuery } from '@mui/material';
import Dropzone from 'domain/shared/Files/Upload/Dropzone';
import Tooltip from 'ui/elements/Tooltip';
import IconButton from 'ui/elements/icons/IconButton';
import ClipIcon from 'ui/elements/icons/ClipIcon';
import { FileRejection } from 'react-dropzone';
import styles from './quill.scss';
import { bluePlanetTheme } from 'ui/theme';
import { logError } from 'util/logging';
import ImageOptionsMenu from './components/ImageOptionsMenu';
import CompanyProfilesDialog from './components/CompanyProfilesDialog';
import config from 'config';
import { v4 as uuidv4 } from 'uuid';
import { notEmpty } from 'util/arrayUtils';
import { useIframeHeights } from 'hooks/useIframeHeights';
import PostDialog from './components/PostDialog';
import { EmbeddedUpdate, UpdateEmbed, useHydrateUpdateBlots } from './components/UpdateBlot';
import { KpiEmbedCard, useHydrateKpiBlots } from './components/KpiBlot';
import { KpiEmbed } from 'domain/kpis/types';
import { createPortal } from 'react-dom';
import KpiEmbedSelector from 'domain/Updates/shared/UpdateForm/KpiEmbedSelector';
import { TinyCompanyDTO } from 'types/company';

// This somehow inserts a single paragraph break in the editor
export const SINGLE_LINE_BREAK = '<p><br></p>';

const Parchment = Quill.import('parchment');

const BottomSectionMobile = styled.div`
  display: flex;
  flex-direction: column;
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  gap: ${halfSpacing};
  padding: ${contentSpacing};
  border-radius: ${bluePlanetTheme.shape.borderRadius}px;
  background-color: ${bluePlanetTheme.bluePlanetPalette.white};
`;

const RichTextActionsMobile = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${quarterSpacing};
  width: 100%;
`;

interface Props {
  className?: string;
  setFieldValue: FormikHelpers<UpdateFormValues>['setFieldValue'];
  setFieldTouched: FormikHelpers<UpdateFormValues>['setFieldTouched'];
  validateField: FormikHelpers<UpdateFormValues>['validateField'];
  handleChange: FormikHandlers['handleChange'];
  getImageUploadUrl: (filename: string) => Promise<CloudinaryUploadSettings>;
  content: string;
  delta: any;
  linkPreview?: string;
  name: string;
  deltaName?: string;
  error?: string | boolean;
  placeholder?: string;
  toolbarType?: ToolbarTypes;
  submitButton?: React.ReactNode;
  onUploadFile: (accepted: File[], rejected: FileRejection[]) => void;
  mentionSearchFunction: (searchQuery: string) => Promise<Mention[]>;
  autoFocus?: boolean;
  initialCursorPosition?: number;
  attachmentSection: React.ReactNode;
  kpiOptions?: {
    company: TinyCompanyDTO;
    pendingKpiEmbed?: KpiEmbed;
    onKpiEmbedInserted: () => void;
  };
}
export type ToolbarTypes = 'basic' | 'advanced' | 'none';

export type Mention = {
  id: string | number;
  value: string;
  imageURL?: string;
  type: 'user' | 'company';
};

interface EditLinkPreview {
  link: string;
  isValidPreview: boolean;
}

const websiteRegex = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,4}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/;

const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'custom-blockquote',
  'list',
  'bullet',
  'link',
  'styled-mention',
  'line-break',
  'image',
  'gridBlot',
  'companyBlot',
  'postBlot',
  'kpiBlot',
  'iframely',
  'advanced',
];

const ExtraRichTextActions = ({
  insertEmoji,
  insertMention,
  mentionSearchFunction: mentionUserSearch,
  onUploadFile,
  children,
}: {
  insertEmoji: (emoji: string) => void;
  insertMention: (mention: Mention) => void;
  mentionSearchFunction: (query: string) => Promise<Mention[]>;
  onUploadFile: (accepted: File[], rejected: FileRejection[]) => void;
  children?: React.ReactNode;
}) => {
  return (
    <>
      <EmojiPicker onSelect={insertEmoji} />
      <MentionSelect onSelect={insertMention} mentionSearchFunction={mentionUserSearch} />
      <div className="u-flex u-flex-space-between u-flex-align-center" style={{ width: '100%' }}>
        <div>
          <Dropzone onDrop={onUploadFile} style={{ width: 'inherit' }}>
            <Tooltip title="Attach files">
              <span>
                <IconButton size="small" color="indigo">
                  <ClipIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Dropzone>
        </div>
        {children}
      </div>
    </>
  );
};

export default function RichTextEditor(props: Props) {
  const quillRef = useRef<ReactQuill>(null);

  const [inProgress, setInProgress] = useState(false);
  const [isIframelyDialogOpen, setIsIframelyDialogOpen] = useState(false);
  const [selection, setSelection] = useState(1);
  const [currentSelection, setCurrentSelection] = useState(0);
  const [linkPreviewState, setLinkPreviewState] = useState<{ link: string; isValidPreview: boolean } | undefined>(
    props.linkPreview ? { link: props.linkPreview, isValidPreview: true } : undefined,
  );

  const closeIframelyDialog = () => setIsIframelyDialogOpen(false);
  const getCurrentSelectionIndex = () => {
    const editor = quillRef.current?.getEditor();
    const selection = editor?.getSelection();
    return (selection && selection.index) || (editor && editor.getLength()) || 1;
  };

  const openIframelyDialog = async () => {
    setSelection(getCurrentSelectionIndex());
    setIsIframelyDialogOpen(true);
  };
  const [selectedImage, setSelectedImage] = useState<{ element: HTMLElement; isFirstChild: boolean } | null>(null);
  const [companyEmbedDialogState, setCompanyEmbedDialogState] = useState<{
    isOpen: boolean;
    element?: HTMLElement;
  }>({ isOpen: false });
  const [postDialogState, setPostDialogState] = useState<{
    isOpen: boolean;
    element?: HTMLElement;
  }>({ isOpen: false });
  const [kpiDialogState, setKpiDialogState] = useState<{
    isOpen: boolean;
    element?: HTMLElement;
    existingEmbed?: KpiEmbed;
    cursorPosition?: number;
  }>({ isOpen: false });

  const handleCloseMenu = () => {
    setSelectedImage(null);
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(bluePlanetTheme.breakpoints.down('sm'));

  const { userProfileIdToShow, setUserProfileIdToShow, companyProfileIdToShow, setCompanyProfileIdToShow } =
    useClickableMentions(
      containerRef,
      quillRef,
      // mention click handlers will no longer work when the audiences change
      [quillRef],
    );

  const handleChangeFunc = (content: string, delta: any, source: any, editor: any) => {
    // we don't want inline images to be added as link preview in the bottom of the update

    const contentWithoutImagesAndIframes = content
      .replace(/<img.*?[^\>]+>/g, '')
      .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '');

    const firstLink = contentWithoutImagesAndIframes.match(websiteRegex);
    if (firstLink && firstLink[0] && (linkPreviewState === undefined || !linkPreviewState.isValidPreview)) {
      setLinkPreviewState({ link: firstLink[0], isValidPreview: false });
    }

    props.setFieldValue(props.name, editor.getHTML());

    if (props.deltaName) {
      props.handleChange({
        target: {
          id: props.deltaName,
          value: editor.getContents(),
        },
      });
      props.setFieldTouched(props.deltaName);
    }
  };
  useEffect(() => {
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();
    const handleMessage = (event: MessageEvent) => {
      if (event.origin === config.PORTAL_URL && event.data.type === 'iframeClicked') {
        const iframes = quill.root.querySelectorAll('iframe');
        let clickedBlotNode = null;

        iframes.forEach(iframe => {
          if (iframe.src.includes(event.data.href)) {
            clickedBlotNode = iframe.closest('.ql-company-embed');
          }
        });

        if (clickedBlotNode) {
          const blot = Quill.find(clickedBlotNode);
          if (blot) {
            setCompanyEmbedDialogState({ isOpen: true, element: clickedBlotNode });
          }
        }
      }
    };

    const handleKpiEmbedClick = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.node && customEvent.detail?.embed) {
        setKpiDialogState({
          isOpen: true,
          element: customEvent.detail.node,
          existingEmbed: customEvent.detail.embed,
        });
      }
    };

    window.addEventListener('message', handleMessage);
    quill.root.addEventListener('kpi-embed-click', handleKpiEmbedClick);

    return () => {
      window.removeEventListener('message', handleMessage);
      quill.root.removeEventListener('kpi-embed-click', handleKpiEmbedClick);
    };
  }, []);

  useHydrateUpdateBlots(quillRef);

  const handleMentionFunc = async function (searchTerm: any, renderList: any) {
    const result = await props.mentionSearchFunction(searchTerm);
    renderList(result.length !== 0 ? [{ id: 'header', value: 'header', disabled: true }, ...result] : [], searchTerm);
  };

  function handleLinebreak(range: any, context: any) {
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();
    const currentLeaf = quill.getLeaf(range.index)[0];
    const nextLeaf = quill.getLeaf(range.index + 1)[0];

    quill.insertEmbed(range.index, 'line-break', true, 'user');

    if (nextLeaf === null || currentLeaf.parent !== nextLeaf.parent) {
      quill.insertEmbed(range.index, 'line-break', true, 'user');
    }
    quill.setSelection(range.index + 1, 'silent');

    Object.keys(context.format).forEach(name => {
      if (Parchment.query(name, Parchment.Scope.BLOCK)) return;
      if (Array.isArray(context.format[name])) return;
      if (name === 'link') return;
      quill.format(name, context.format[name], 'user');
    });
  }

  function lineBreakMatcher() {
    const Delta = Quill.import('delta');
    const newDelta = new Delta();
    newDelta.insert({ 'line-break': '' });
    return newDelta;
  }

  const debouncedChangeFunc = debounce(handleChangeFunc, 200);
  const debouncedMentionFunc = debounce(handleMentionFunc, 200);
  useIframeHeights(containerRef);

  const setCursorPosition = (index: number) => {
    const editor = quillRef.current && quillRef.current.getEditor();
    editor?.setSelection(index, 0);
    editor?.focus();
  };

  const insertIframe = async (src: string, isRawIframe: boolean) => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      if (!src) {
        return;
      }
      const api = IframelyApi();
      let data = { html: src };
      if (!isRawIframe) {
        data = await api.get(src);
      }

      const index = selection;
      editor.insertEmbed(index, 'iframely', { value: data.html }, 'user');
      editor.insertText(index + 1, '\n', 'silent');
      setCursorPosition(index + 2);
    }
  };

  const onInsertIframe = (embedUrl: string) => {
    const cleaned = stripEmbed(embedUrl);
    const parsed = parseEmbed(cleaned);
    if (parsed) {
      insertIframe(parsed, isIframe(cleaned));
      closeIframelyDialog();
    } else {
      alert('Sorry, we only support iframe and link embeds at the moment');
    }
  };

  const insertEmoji = (emoji: string) => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const index = currentSelection;

      editor.insertText(index + 0, emoji, 'user');
      // For some reason, setting focus right after the emoji picker dialog closes doesn't work.
      // Using a short timeout do. This might break for some user, and might need to be solved in another way
      setTimeout(() => {
        setCursorPosition(index + 2);
        editor.focus();
      }, 50);
    }
  };

  const insertAndUploadImage = async (filename: string, file: Blob) => {
    setInProgress(true);
    try {
      const result = await props.getImageUploadUrl(filename);
      const data = await uploadToCloudinary(result, file);
      setInProgress(false);
      return Promise.resolve(data);
    } catch (e) {
      logError(e);
      setInProgress(false);
      return Promise.reject();
    }
  };

  // Create the image clipboard matcher with dependencies
  const imageClipboardMatcher = createImageClipboardMatcher({
    insertAndUploadImage,
    quillRef,
  });

  const insertMention = (mention: Mention) => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const index = currentSelection;

      editor.insertEmbed(
        index,
        'styled-mention',
        { index, denotationChar: '@', id: mention.id, value: mention.value, type: mention.type },
        'user',
      );

      editor.insertText(index + 1, ' ', 'silent');

      setTimeout(() => {
        setCursorPosition(index + 2);
        editor.focus();
      }, 50);
    }
  };

  const insertProfiles = (urls: string[], isDetailed: boolean, isGrid: boolean) => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const index = currentSelection;

      const slugs = urls
        .map(url => {
          const match = url.match(/\/companies\/([\w-]+)/);
          return match && match[1];
        })
        .filter(notEmpty);
      const ids = slugs.map(() => uuidv4());

      if (companyEmbedDialogState?.element) {
        const blot = Quill.find(companyEmbedDialogState.element);
        if (blot) {
          blot.format('slugs', { slugs, ids, isDetailed, isGrid });
          return;
        }
      } else {
        editor.insertEmbed(index, 'companyBlot', { ids, slugs, isDetailed, isGrid }, 'user');
        setCursorPosition(index + 2);
      }
    }
  };

  const insertPost = (update: EmbeddedUpdate) => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const index = currentSelection;

      if (postDialogState?.element) {
        const blot = Quill.find(postDialogState.element);
        if (blot) {
          blot.format('slug', update);
          return;
        }
      } else {
        editor.insertEmbed(index, 'postBlot', update, 'user');
        setCursorPosition(index + 2);
      }
    }
  };

  const insertKpi = (embed: KpiEmbed) => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();

      if (kpiDialogState?.element) {
        // Editing existing embed
        const blot = Quill.find(kpiDialogState.element);
        if (blot) {
          blot.format('embed', { embed });
          // Mutation observer will automatically detect the attribute change
          return;
        }
      }

      // Inserting new embed - use saved cursor position from when dialog was opened
      const index = kpiDialogState?.cursorPosition ?? currentSelection;
      editor.insertEmbed(index, 'kpiBlot', { embed }, 'user');
      editor.insertText(index + 1, '\n', 'silent');
      setCursorPosition(index + 2);
    }
  };

  const modules = useMemo(
    () => ({
      keyboard: {
        bindings: {
          // default behaviour is that tabbing inside editor will insert a tab character
          // disabling this will make the tab key change focus to the next element in the dom.
          tab: false,
          handleShiftEnter: {
            key: 13,
            shiftKey: true,
            // Shift + Enter inserts a line break
            handler: handleLinebreak,
          },
          handleEnter: {
            key: 13,
            // When inside a blockquote, pressing enter finishes the blockquote and moves the cursor to the next line
            handler: function (range: Range) {
              if (!quillRef.current || !range) return;
              const quill = quillRef.current.getEditor();
              const format = quill.getFormat(range.index);
              if (format['custom-blockquote']) {
                quill.insertText(range.index, '\n');
                quill.setSelection(range.index + 1, 0);
                quill.format('custom-blockquote', false);
                return false;
              } else {
                return true;
              }
            },
          },
        },
      },
      magicUrl: true,
      toolbar: {
        container: '#toolbar',
        handlers: {
          iframely: openIframelyDialog,
          'company-profiles': () => setCompanyEmbedDialogState({ isOpen: true }),
          'post-embed': () => setPostDialogState({ isOpen: true }),
          'kpi-embed': () => {
            // Only open dialog if kpiOptions is provided
            if (!props.kpiOptions) return;
            const editor = quillRef.current?.getEditor();
            const range = editor?.getSelection();
            const position = range ? range.index : currentSelection;
            setKpiDialogState({ isOpen: true, cursorPosition: position });
          },
          'custom-blockquote': function () {
            const quill = quillRef.current?.getEditor();
            const range = quill?.getSelection();
            if (range) {
              const format = quill?.getFormat(range.index);
              if (format && format['custom-blockquote']) {
                quill?.format('custom-blockquote', false);
              } else {
                quill?.format('custom-blockquote', true);
              }
            }
          },
        },
      },
      clipboard: {
        matchers: [
          ['BR', lineBreakMatcher],
          ['IMG', imageClipboardMatcher],
        ],
        matchVisual: false,
      },
      imageUpload: {
        uploadFunc: insertAndUploadImage,
      },
      imageMenu: { onImageSelect: setSelectedImage },
      mention: mentionModule(debouncedMentionFunc),
    }),
    [],
  );

  useEffect(() => {
    if (props.autoFocus) {
      const position = props.initialCursorPosition ?? (quillRef.current?.getEditor().getLength() || 0);
      setCursorPosition(position);
      quillRef.current?.editor?.focus();
    }
  }, [props.autoFocus, props.initialCursorPosition]);
  // This transforms the string delta into quill delta object on first render.
  // We need to do this for operations on the delta to work properly. (E.g. clearing mentions)
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (editor && props.deltaName) {
      props.handleChange({
        target: {
          id: props.deltaName,
          value: editor.getContents(),
        },
      });
    }
  }, [quillRef]);

  const embeddedUpdateMounts = useHydrateUpdateBlots(quillRef);
  const embeddedKpiMounts = useHydrateKpiBlots(quillRef);
  const [hasInsertedKpiEmbed, setHasInsertedKpiEmbed] = useState(false);

  // Auto-insert pending KPI embed at cursor position
  useEffect(() => {
    if (props.kpiOptions?.pendingKpiEmbed && quillRef.current && !hasInsertedKpiEmbed) {
      setHasInsertedKpiEmbed(true);
      // Delay to ensure Quill is fully initialized
      setTimeout(() => {
        const editor = quillRef.current?.getEditor();
        if (editor) {
          const selection = editor.getSelection();
          // Insert at cursor position, or at the start (0) if no selection (new post)
          const position = selection ? selection.index : 0;
          editor.insertEmbed(position, 'kpiBlot', { embed: props.kpiOptions?.pendingKpiEmbed }, 'user');
          editor.insertText(position + 1, '\n', 'silent');
          // Move cursor after the inserted KPI
          editor.setSelection(position + 1, 0);
          // Notify parent that embed has been inserted
          props.kpiOptions?.onKpiEmbedInserted?.();
        }
      }, 200);
    }
  }, [props.kpiOptions?.pendingKpiEmbed, hasInsertedKpiEmbed, props.kpiOptions?.onKpiEmbedInserted]);

  return (
    <>
      <BlockUi visible={inProgress} />
      <div id="postEditorContainer" ref={containerRef}>
        <AdvancedToolbar
          showKpiButton={!!props.kpiOptions}
          desktopActions={
            <ExtraRichTextActions
              insertEmoji={insertEmoji}
              insertMention={insertMention}
              onUploadFile={props.onUploadFile}
              mentionSearchFunction={props.mentionSearchFunction}
            />
          }
        />

        <EmbedDialog onConfirm={onInsertIframe} isDialogOpen={isIframelyDialogOpen} closeDialog={closeIframelyDialog} />
        <CompanyProfilesDialog
          isOpen={companyEmbedDialogState?.isOpen}
          closeDialog={() => {
            setCompanyEmbedDialogState({ isOpen: false });
          }}
          selectedElement={companyEmbedDialogState?.element}
          onConfirm={(urls, isDetailed, isGrid) => {
            insertProfiles(urls, isDetailed, isGrid);
            setCompanyEmbedDialogState({ isOpen: false });
          }}
        />
        <PostDialog
          isOpen={postDialogState?.isOpen}
          closeDialog={() => {
            setPostDialogState({ isOpen: false });
          }}
          onConfirm={({ updateId, location: context }) => {
            insertPost({ updateId, location: context });
            setPostDialogState({ isOpen: false });
          }}
        />
        {props.kpiOptions?.company && kpiDialogState.isOpen && (
          <KpiEmbedSelector
            company={props.kpiOptions.company}
            existingEmbed={kpiDialogState.existingEmbed}
            onKpiSelected={embed => {
              insertKpi(embed);
              setKpiDialogState({
                isOpen: false,
                element: undefined,
                existingEmbed: undefined,
                cursorPosition: undefined,
              });
            }}
            onClose={() =>
              setKpiDialogState({
                isOpen: false,
                element: undefined,
                existingEmbed: undefined,
                cursorPosition: undefined,
              })
            }
          />
        )}
        <ReactQuill
          className={styles.editor}
          ref={quillRef}
          onChangeSelection={selection => {
            if (selection) {
              setCurrentSelection(selection.index);
            }
          }}
          onChange={debouncedChangeFunc}
          placeholder={props.placeholder || 'Write your story'}
          defaultValue={!isEmpty(props.delta) ? props.delta : props.content}
          modules={{ ...modules }}
          formats={formats}
          preserveWhitespace
          // We need to account for the fixed toolbar at the bottom
          style={{ paddingBottom: isMobile ? '80px' : undefined }}
        />
        {/* Hydrate updates */}
        {embeddedUpdateMounts
          .filter(m => m.updateId)
          .map(m =>
            createPortal(<UpdateEmbed key={m.updateId} updateId={m.updateId} slug={m.slug} type={m.type} />, m.node),
          )}
        {/* Hydrate KPI embeds */}
        {embeddedKpiMounts.map((m, i) =>
          createPortal(<KpiEmbedCard key={`${m.embed.kpiId}-${i}`} embed={m.embed} isEditable={true} />, m.node),
        )}
      </div>
      {selectedImage && (
        <ImageOptionsMenu
          quillRef={quillRef}
          imgElement={selectedImage.element}
          isFirstChild={selectedImage.isFirstChild}
          quillRoot={containerRef.current}
          onClose={handleCloseMenu}
        />
      )}
      {linkPreviewState && (
        <div className="u-half-spacing-top">
          <EditLinkPreview
            link={linkPreviewState.link}
            onRemove={() => {
              setLinkPreviewState(undefined);
              props.setFieldValue('linkPreview', '');
            }}
            onLinkValid={link => {
              setLinkPreviewState({ link, isValidPreview: true });
              props.setFieldValue('linkPreview', link);
            }}
          />
        </div>
      )}
      {props.error && <FormHelperText error>{props.error}</FormHelperText>}
      <Hidden smUp>
        <BottomSectionMobile>
          {props.attachmentSection}
          <RichTextActionsMobile>
            <ExtraRichTextActions
              onUploadFile={props.onUploadFile}
              mentionSearchFunction={props.mentionSearchFunction}
              insertEmoji={insertEmoji}
              insertMention={insertMention}
            >
              {props.submitButton}
            </ExtraRichTextActions>
          </RichTextActionsMobile>
        </BottomSectionMobile>
      </Hidden>
      {userProfileIdToShow && (
        <UserProfileDialog cwUserId={userProfileIdToShow} onClose={() => setUserProfileIdToShow(undefined)} />
      )}
      {companyProfileIdToShow && (
        <PublicProfileDialog companyId={companyProfileIdToShow} onClose={() => setCompanyProfileIdToShow(undefined)} />
      )}
      <Hidden smDown>{props.attachmentSection}</Hidden>
    </>
  );
}

Quill.register('modules/magicUrl', MagicUrl);
